import { useQuery } from "@apollo/client";
import { Delete, Edit } from "@mui/icons-material";
import { DateTime } from "luxon";
import { ReactElement, useCallback } from "react";

import {
  DeletePersonalizedGifMutation,
  DeletePersonalizedGifMutationVariables,
  ListPersonalizedGifsQuery,
  ListPersonalizedGifsQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import Table from "~frontend/components/Table";
import { GifPreview } from "~frontend/components/personalizedGifs/GifPreview";
import {
  DELETE_PERSONALIZED_GIF,
  LIST_PERSONALIZED_GIFS,
} from "~frontend/graphql/personalizedGifs";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMutation from "~frontend/util/useMutation";

import { CampaignContactFieldChip } from "./CampaignContactFieldChip";
import { PersonalizedGif } from "./types";

export function GifsTable({
  onOpenEditDialog,
}: {
  onOpenEditDialog: (personalizedGif: PersonalizedGif) => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const { data, loading } = useQuery<
    ListPersonalizedGifsQuery,
    ListPersonalizedGifsQueryVariables
  >(LIST_PERSONALIZED_GIFS, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  const [deletePersonalizedGif] = useMutation<
    DeletePersonalizedGifMutation,
    DeletePersonalizedGifMutationVariables
  >(DELETE_PERSONALIZED_GIF, {
    refetchQueries: [LIST_PERSONALIZED_GIFS],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error deleting GIF",
  });

  const handleDeletePersonalizedGif = useCallback(
    async (personalizedGif: PersonalizedGif) => {
      await deletePersonalizedGif({
        variables: { input: { personalizedGifId: personalizedGif.id } },
      });
    },
    [deletePersonalizedGif]
  );

  if (loading || !data) {
    return <Loading />;
  }

  return (
    <Table
      rows={data.listPersonalizedGifs}
      emptyState="You haven't created any personalized GIFs yet."
      columns={[
        {
          key: "gif",
          title: "GIF",
          renderValue: row => <GifPreview personalizedGifId={row.id} />,
          align: "left",
        },
        {
          key: "name",
          title: "Name",
          renderValue: row => row.name,
          align: "left",
          sx: { verticalAlign: "top" },
        },
        {
          key: "campaignContactFields",
          title: "Custom Fields",
          renderValue: row => (
            <FlexBox flexDirection="column" alignItems="flex-start" gap="8px">
              {row.creatomateTemplate.requiredContactFields.map(field => (
                <CampaignContactFieldChip
                  key={field.name}
                  fieldName={field.name}
                />
              ))}
            </FlexBox>
          ),
          align: "left",
          sx: { verticalAlign: "top" },
        },
        {
          key: "created",
          title: "Created",
          renderValue: row =>
            `${DateTime.fromMillis(row.createdAt).toLocaleString(
              DateTime.DATETIME_SHORT
            )}, by ${row.createdBy.fullName}`,
          align: "left",
          sx: { verticalAlign: "top" },
        },
      ]}
      rowActions={[
        {
          id: "edit",
          icon: <Edit />,
          onClick: onOpenEditDialog,
        },
        {
          id: "delete",
          icon: <Delete />,
          confirm: row => ({
            title: `Delete ${row.name}`,
            description: `Permanently delete ${row.name}?`,
          }),
          onClick: handleDeletePersonalizedGif,
        },
      ]}
      actionSx={{ verticalAlign: "top" }}
      noFooter
    />
  );
}
